/**
 * Agreements Tab Component
 *
 * Shows score agreements for a team member in a table.
 * Opens AgreementDetailModal for viewing and editing.
 * Supports both evaluation-linked and standalone agreements.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { evaluationsApi, standaloneAgreementsApi, type ScoreAgreement, type ScoreAgreementStatus, type StandaloneAgreementInput } from '../../lib/evaluationsApi';
import { agreementTypesApi } from '../../lib/settingsApi';
import { useToast } from '../../components/Toast';
import { AgreementDetailModal } from '../agreements/AgreementDetailModal';

interface AgreementsTabProps {
  employeeId: number;
  isReadOnly?: boolean;
}

const STATUS_LABELS: Record<ScoreAgreementStatus, { label: string; color: string }> = {
  concept: { label: 'Concept', color: 'bg-yellow-100 text-yellow-800' },
  definitief: { label: 'Definitief', color: 'bg-blue-100 text-blue-800' },
  afgerond: { label: 'Afgerond', color: 'bg-green-100 text-green-800' },
  geannuleerd: { label: 'Geannuleerd', color: 'bg-gray-100 text-gray-600' },
};

export function AgreementsTab({ employeeId, isReadOnly = false }: AgreementsTabProps) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [selectedAgreementId, setSelectedAgreementId] = useState<number | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newForm, setNewForm] = useState<StandaloneAgreementInput>({
    agreementTypeId: 0,
    beschrijving: '',
    deadline: '',
    status: 'concept',
    isEmployeeActionHolder: false,
  });

  // Fetch agreements for the employee
  const { data: agreementsResponse, isLoading } = useQuery({
    queryKey: ['scoreAgreements', 'employee', employeeId],
    queryFn: () => evaluationsApi.getScoreAgreementsForEmployee(employeeId),
    enabled: !!employeeId,
  });

  // Fetch agreement types for new agreement modal
  const { data: agreementTypes = [] } = useQuery({
    queryKey: ['agreementTypes'],
    queryFn: agreementTypesApi.list,
  });

  const agreements = agreementsResponse?.data || [];

  // Create standalone agreement mutation
  const createAgreement = useMutation({
    mutationFn: (input: StandaloneAgreementInput) => standaloneAgreementsApi.create(employeeId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scoreAgreements', 'employee', employeeId] });
      queryClient.invalidateQueries({ queryKey: ['agreements', 'team'] });
      setShowNewModal(false);
      setNewForm({ agreementTypeId: 0, beschrijving: '', deadline: '', status: 'concept', isEmployeeActionHolder: false });
      showToast('Afspraak aangemaakt', 'success');
    },
    onError: () => {
      showToast('Fout bij aanmaken', 'error');
    },
  });

  const handleCreateNew = async () => {
    if (!newForm.agreementTypeId || !newForm.beschrijving.trim()) return;

    await createAgreement.mutateAsync({
      agreementTypeId: newForm.agreementTypeId,
      beschrijving: newForm.beschrijving,
      deadline: newForm.deadline || undefined,
      status: newForm.status,
      isEmployeeActionHolder: newForm.isEmployeeActionHolder,
    });
  };

  const openNewModal = () => {
    // Set default agreement type if available
    const firstType = agreementTypes[0];
    if (agreementTypes.length > 0 && firstType) {
      setNewForm((f) => ({ ...f, agreementTypeId: firstType.id }));
    }
    setShowNewModal(true);
  };

  const handleRowClick = (agreement: ScoreAgreement) => {
    setSelectedAgreementId(agreement.id);
  };

  const handleModalClose = () => {
    setSelectedAgreementId(null);
    // Refresh data after modal closes (in case updates were made)
    queryClient.invalidateQueries({ queryKey: ['scoreAgreements', 'employee', employeeId] });
    queryClient.invalidateQueries({ queryKey: ['agreements', 'team'] });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Afspraken laden...</span>
      </div>
    );
  }

  return (
    <div data-testid="agreements-tab">
      {/* Header with new agreement button */}
      {!isReadOnly && (
        <div className="flex justify-end mb-4">
          <button
            onClick={openNewModal}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nieuwe Afspraak
          </button>
        </div>
      )}

      {agreements.length > 0 ? (
        <div className="space-y-3">
          {agreements.map((agreement) => (
            <div
              key={agreement.id}
              data-testid="agreement-row"
              className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 cursor-pointer transition-colors"
              onClick={() => handleRowClick(agreement)}
            >
              <div className="flex items-start justify-between gap-4">
                {/* Left side: main content */}
                <div className="flex-1 min-w-0">
                  {/* Type and status badges */}
                  <div className="flex items-center gap-2 mb-2">
                    {agreement.agreementTypeIcon && (
                      <span className="text-lg">{agreement.agreementTypeIcon}</span>
                    )}
                    <span className="font-medium text-gray-900">
                      {agreement.agreementTypeName || 'Afspraak'}
                    </span>
                    <span
                      className={`inline-block px-2 py-0.5 text-xs rounded-full ${
                        STATUS_LABELS[agreement.status].color
                      }`}
                    >
                      {STATUS_LABELS[agreement.status].label}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-700 mb-2 line-clamp-2">
                    {agreement.beschrijving}
                  </p>

                  {/* Meta info row */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                    {/* Date */}
                    <span className="inline-flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {formatDate(agreement.datumVastgelegd)}
                    </span>

                    {/* Deadline */}
                    {agreement.deadline && (
                      <span className="inline-flex items-center gap-1 text-orange-600">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Deadline: {formatDate(agreement.deadline)}
                      </span>
                    )}

                    {/* Context */}
                    {agreement.isStandalone ? (
                      <span className="inline-flex items-center gap-1 text-purple-600">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        Losse afspraak
                      </span>
                    ) : (
                      <>
                        {agreement.taskName && (
                          <span className="text-blue-600">Taak: {agreement.taskName}</span>
                        )}
                        {agreement.competencyName && (
                          <span className="text-orange-600">Competentie: {agreement.competencyName}</span>
                        )}
                        {!agreement.taskName && !agreement.competencyName && agreement.evaluationPeriod && (
                          <span>Beoordeling {formatDate(agreement.evaluationPeriod.start)}</span>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Right side: action holder badge */}
                <div className="flex-shrink-0">
                  {agreement.isEmployeeActionHolder ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                      Medewerker
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                      Leidinggevende
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500 mb-4">
            Nog geen afspraken voor deze medewerker.
          </p>
          {!isReadOnly && (
            <button
              onClick={openNewModal}
              className="inline-flex items-center gap-2 px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Eerste afspraak toevoegen
            </button>
          )}
        </div>
      )}

      {/* New Agreement Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Nieuwe Afspraak</h3>

            <div className="space-y-4">
              {/* Agreement Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type afspraak *
                </label>
                <select
                  value={newForm.agreementTypeId}
                  onChange={(e) => setNewForm((f) => ({ ...f, agreementTypeId: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={0} disabled>Selecteer type...</option>
                  {agreementTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.icon} {type.displayName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beschrijving *
                </label>
                <textarea
                  value={newForm.beschrijving}
                  onChange={(e) => setNewForm((f) => ({ ...f, beschrijving: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Beschrijf de afspraak..."
                />
              </div>

              {/* Deadline (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deadline (optioneel)
                </label>
                <input
                  type="date"
                  value={newForm.deadline || ''}
                  onChange={(e) => setNewForm((f) => ({ ...f, deadline: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={newForm.status}
                  onChange={(e) => setNewForm((f) => ({ ...f, status: e.target.value as ScoreAgreementStatus }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="concept">Concept</option>
                  <option value="definitief">Definitief</option>
                </select>
              </div>

              {/* Employee Action Holder */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newForm.isEmployeeActionHolder || false}
                    onChange={(e) => setNewForm((f) => ({ ...f, isEmployeeActionHolder: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                  />
                  <div>
                    <span className="block text-sm font-medium text-amber-800">
                      Medewerker is actiehouder
                    </span>
                    <span className="block text-xs text-amber-600 mt-0.5">
                      De medewerker is verantwoordelijk voor opvolging van deze afspraak
                    </span>
                  </div>
                </label>
              </div>
            </div>

            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowNewModal(false);
                  setNewForm({ agreementTypeId: 0, beschrijving: '', deadline: '', status: 'concept', isEmployeeActionHolder: false });
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuleren
              </button>
              <button
                onClick={handleCreateNew}
                disabled={!newForm.agreementTypeId || !newForm.beschrijving.trim() || createAgreement.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {createAgreement.isPending ? 'Aanmaken...' : 'Aanmaken'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Agreement Detail Modal */}
      {selectedAgreementId && (
        <AgreementDetailModal
          agreementId={selectedAgreementId}
          onClose={handleModalClose}
          canEdit={!isReadOnly}
        />
      )}
    </div>
  );
}
